import type {
  AppInsightsSummary,
  E2eResults,
  Env,
  RecentCorrelations,
  ResubmitDlqRequest,
  ResubmitDlqResult,
  ResubmitLogicAppRunRequest,
  ResubmitLogicAppRunResult,
  ServiceBusSummary,
  TracesByCorrelation,
  WorkflowActionPayload,
  WorkflowDurations,
  WorkflowRunsBreakdown,
  WorkflowRunsByName,
  WorkflowRunsSummary,
} from '../types';
import { getCached, setCache } from './cache';

// Thin fetch wrapper. All dashboard backend calls go through /api/dashboard/*
// which is auth-gated by staticwebapp.config.json (requires authenticated user).
export function apiUrl(path: string, params: Record<string, string | number> = {}): string {
  const stringParams: Record<string, string> = {};
  for (const [k, v] of Object.entries(params)) stringParams[k] = String(v);
  const qs = new URLSearchParams(stringParams).toString();
  return `/api/dashboard/${path}${qs ? `?${qs}` : ''}`;
}

async function apiGet<T>(path: string, params: Record<string, string | number> = {}): Promise<T> {
  const url = apiUrl(path, params);
  const res = await fetch(url, {
    headers: { Accept: 'application/json' },
    credentials: 'include',
  });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`GET ${url} failed: ${res.status} ${body}`);
  }
  const data = (await res.json()) as T;
  setCache(url, data);
  return data;
}

// Prefetch helper — fire-and-forget a GET so a subsequent detail page load
// can read the response from localStorage instead of waiting on the network.
export function prefetch(path: string, params: Record<string, string | number> = {}): void {
  apiGet(path, params).catch(() => {
    // Swallow errors — this is a nice-to-have warm-up, the detail page will
    // retry on its own useEffect.
  });
}

// Read-through helper. Returns cached data synchronously if fresh (so detail
// pages can render immediately on open), while callers still await the live
// fetch for background revalidation.
export function getCachedResponse<T>(path: string, params: Record<string, string | number> = {}): T | null {
  return getCached<T>(apiUrl(path, params));
}

export function getWorkflowRunsSummary(env: Env): Promise<WorkflowRunsSummary> {
  return apiGet<WorkflowRunsSummary>('workflow-runs-summary', { env });
}

export function getWorkflowRunsBreakdown(env: Env, windowHours = 24): Promise<WorkflowRunsBreakdown> {
  return apiGet<WorkflowRunsBreakdown>('workflow-runs-breakdown', { env, windowHours });
}

export function getAppInsightsSummary(env: Env, windowHours = 24): Promise<AppInsightsSummary> {
  return apiGet<AppInsightsSummary>('app-insights-summary', { env, windowHours });
}

export function getWorkflowDurations(env: Env, windowHours = 24): Promise<WorkflowDurations> {
  return apiGet<WorkflowDurations>('workflow-durations', { env, windowHours });
}

export function getServiceBusSummary(env: Env): Promise<ServiceBusSummary> {
  return apiGet<ServiceBusSummary>('service-bus-summary', { env });
}

export function getE2eResults(env: Env): Promise<E2eResults> {
  return apiGet<E2eResults>('e2e-results', { env });
}

export async function postResubmitDlq(body: ResubmitDlqRequest): Promise<ResubmitDlqResult> {
  const res = await fetch('/api/dashboard/service-bus/resubmit-dlq', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`POST resubmit-dlq failed: ${res.status} ${text}`);
  }
  return (await res.json()) as ResubmitDlqResult;
}

export function getWorkflowRunsByName(
  env: Env,
  workflow: string,
  top = 20,
  status?: string,
): Promise<WorkflowRunsByName> {
  const params: Record<string, string | number> = { env, workflow, top };
  if (status) params.status = status;
  return apiGet<WorkflowRunsByName>('workflow-runs/by-name', params);
}

export function getTracesByCorrelation(
  env: Env,
  correlationId: string,
  windowHours = 24,
): Promise<TracesByCorrelation> {
  return apiGet<TracesByCorrelation>('traces/by-correlation', {
    env,
    correlationId,
    windowHours,
  });
}

export function getWorkflowActionPayload(
  env: Env,
  workflowName: string,
  runId: string,
  actionName: string,
): Promise<WorkflowActionPayload> {
  return apiGet<WorkflowActionPayload>('workflow-action-payload', {
    env,
    workflowName,
    runId,
    actionName,
  });
}

export function getRecentCorrelations(
  env: Env,
  windowHours: number,
  search?: string,
): Promise<RecentCorrelations> {
  const params: Record<string, string | number> = { env, windowHours };
  if (search) params.search = search;
  return apiGet<RecentCorrelations>('recent-correlations', params);
}

export async function postResubmitLogicAppRun(
  body: ResubmitLogicAppRunRequest,
): Promise<ResubmitLogicAppRunResult> {
  const res = await fetch('/api/dashboard/logic-apps/resubmit-run', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(`POST resubmit-run failed: ${res.status} ${text}`);
  }
  return (await res.json()) as ResubmitLogicAppRunResult;
}
